import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import { BarChart3, Check, ChevronDown, Fan, Zap, Wrench, Cpu, Shield, Lamp, Pencil, X, Plus, Trash2, Save, Lock, Unlock } from "lucide-react";
import { listProjects, listProjectPrimaries, getProjectTracker, saveProjectTracker, updateTrackerProgress, listCompanyTrackerTemplates, getCompanyTrackerTemplateById, createCompanyTrackerTemplate, updateCompanyTrackerTemplate, deleteCompanyTrackerTemplate, getMeRbac, listCompanies, toggleTrackerLock } from "@/lib/backend";
import type {
  ProjectSummary,
  ProjectPrimary,
  ProjectTrackerState,
} from "@/lib/types";
import { useUser } from "@clerk/clerk-react";
import { toast } from "@/hooks/use-toast";
import { useWorkspace } from "@/components/layout/workspace-context";

export type Discipline =
  | "Mechanical"
  | "Electrical"
  | "Lighting"
  | "Plumbing"
  | "Fire Protection"
  | "Technology";

const DISCIPLINE_ORDER: Discipline[] = [
  "Mechanical",
  "Electrical",
  "Lighting",
  "Plumbing",
  "Fire Protection",
  "Technology",
];

export interface TrackerTask {
  id: string;
  text: string;
  done: boolean;
  hint?: string;
}
export interface MilestoneData {
  percent: number;
  title: string;
  tasks: TrackerTask[];
}
export interface DisciplineTracker {
  discipline: string;
  milestones: MilestoneData[];
  updatedAt?: string;
}

// ----- Default seed data (milestone structure only, empty task arrays) -----
const defaultMilestones: MilestoneData[] = [
  { percent: 50, title: "50% Progress", tasks: [] },
  { percent: 75, title: "75% Progress", tasks: [] },
  { percent: 90, title: "90% Progress", tasks: [] },
  { percent: 100, title: "100% Progress", tasks: [] },
];

const defaultData: DisciplineTracker[] = [
  { discipline: "Mechanical", milestones: defaultMilestones.map((m) => ({ ...m, tasks: [] })) },
  { discipline: "Electrical", milestones: defaultMilestones.map((m) => ({ ...m, tasks: [] })) },
  { discipline: "Lighting", milestones: defaultMilestones.map((m) => ({ ...m, tasks: [] })) },
  { discipline: "Plumbing", milestones: defaultMilestones.map((m) => ({ ...m, tasks: [] })) },
  { discipline: "Fire Protection", milestones: defaultMilestones.map((m) => ({ ...m, tasks: [] })) },
  { discipline: "Technology", milestones: defaultMilestones.map((m) => ({ ...m, tasks: [] })) },
];

function cloneTrackerData(data: DisciplineTracker[]): DisciplineTracker[] {
  return data.map((disc) => ({
    ...disc,
    milestones: disc.milestones.map((milestone) => ({
      ...milestone,
      tasks: milestone.tasks.map((task) => ({ ...task })),
    })),
  }));
}

function mergeTrackerState(state: ProjectTrackerState | null | undefined): DisciplineTracker[] {
  // No saved state → fall back to hardcoded defaults (brand-new project).
  if (!state || !Array.isArray(state.disciplines) || state.disciplines.length === 0) {
    return cloneTrackerData(defaultData);
  }

  // Build a lookup of default disciplines so we can append any that are missing from saved state.
  const defaultByDiscipline = new Map<string, DisciplineTracker>();
  for (const disc of defaultData) {
    defaultByDiscipline.set(disc.discipline, disc);
  }

  const coveredDisciplines = new Set<string>();

  // Saved data is the structural authority: iterate saved disciplines, preserving full task trees.
  const result: DisciplineTracker[] = state.disciplines.map((savedDisc) => {
    coveredDisciplines.add(savedDisc.discipline);

    // Default milestones for fallback title resolution only.
    const defaultDisc = defaultByDiscipline.get(savedDisc.discipline);
    const defaultMilestoneByPercent = new Map<number, MilestoneData>();
    if (defaultDisc) {
      for (const m of defaultDisc.milestones) {
        defaultMilestoneByPercent.set(m.percent, m);
      }
    }

    const milestones: MilestoneData[] = savedDisc.milestones.map((savedMilestone) => {
      const defaultMilestone = defaultMilestoneByPercent.get(savedMilestone.percent);

      const tasks: TrackerTask[] = savedMilestone.tasks.map((savedTask) => ({
        id: savedTask.id,
        text: savedTask.text,
        done: Boolean(savedTask.done),
        hint: savedTask.hint ?? undefined,
      }));

      return {
        percent: savedMilestone.percent,
        title: savedMilestone.title || defaultMilestone?.title || `${savedMilestone.percent}% Progress`,
        tasks,
      };
    });

    return {
      discipline: savedDisc.discipline,
      milestones,
      updatedAt: savedDisc.updated_at ?? undefined,
    };
  });

  // Append any default disciplines NOT present in saved state (backwards compat — e.g. a new
  // discipline was added to defaults after the project already had saved tracker data).
  for (const defaultDisc of defaultData) {
    if (!coveredDisciplines.has(defaultDisc.discipline)) {
      result.push({
        ...defaultDisc,
        milestones: defaultDisc.milestones.map((milestone) => ({
          ...milestone,
          tasks: milestone.tasks.map((task) => ({ ...task, done: false })),
        })),
      });
    }
  }

  return result;
}

function toProjectTrackerState(
  projectId: string,
  data: DisciplineTracker[],
  currentUpdatedAt?: string | null,
  progressState: "read_only" | "active" = "active",
): ProjectTrackerState {
  return {
    project_id: projectId,
    updated_at: currentUpdatedAt ?? null,
    progress_state: progressState,
    disciplines: data.map((disc) => ({
      discipline: disc.discipline,
      updated_at: disc.updatedAt ?? null,
      milestones: disc.milestones.map((milestone) => ({
        percent: milestone.percent,
        title: milestone.title,
        tasks: milestone.tasks.map((task) => ({
          id: task.id,
          text: task.text,
          done: task.done,
          hint: task.hint ?? null,
        })),
      })),
    })),
  };
}

const DISCIPLINE_META: Record<
  Discipline,
  { icon: React.ReactNode; accent: string; tabTint: string }
> = {
  Mechanical: { icon: <Fan className="h-4 w-4" />, accent: "from-blue-500/30", tabTint: "text-blue-300" },
  Electrical: { icon: <Zap className="h-4 w-4" />, accent: "from-yellow-400/30", tabTint: "text-yellow-300" },
  Plumbing: { icon: <Wrench className="h-4 w-4" />, accent: "from-[#7b2ee6]/40", tabTint: "text-[#b396ff]" },
  Technology: { icon: <Cpu className="h-4 w-4" />, accent: "from-cyan-400/30", tabTint: "text-cyan-300" },
  "Fire Protection": { icon: <Shield className="h-4 w-4" />, accent: "from-red-500/25", tabTint: "text-red-300" },
  Lighting: { icon: <Lamp className="h-4 w-4" />, accent: "from-amber-400/25", tabTint: "text-amber-300" },
};

const DISCIPLINE_PREFIX: Record<Discipline, string> = {
  Mechanical: "me",
  Electrical: "el",
  Lighting: "lt",
  Plumbing: "pl",
  "Fire Protection": "fp",
  Technology: "te",
};

function deriveMilestoneMetrics(milestones: MilestoneData[]) {
  const sorted = [...milestones].sort((a, b) => a.percent - b.percent);
  const ticks: Record<number, number> = {};
  const weights: Record<number, number> = {};
  sorted.forEach((m, i) => {
    const prev = i === 0 ? 0 : sorted[i - 1].percent;
    ticks[m.percent] = m.percent;
    weights[m.percent] = (m.percent - prev) / 100;
  });
  return { ticks, weights };
}

// --- Helpers ---
const cx = (...v: Array<string | false | null | undefined>) => v.filter(Boolean).join(" ");

function formatUpdated(iso?: string) {
  if (!iso) return "just now";
  try {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    if (diffMs <= 0) return "just now";

    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    if (diffMinutes < 1) return "just now";
    if (diffMinutes < 60) return `${diffMinutes} min ago`;

    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    if (diffHours < 24) return `${diffHours} hr${diffHours === 1 ? "" : "s"} ago`;

    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 1) return "1 day ago";
    return `${diffDays} days ago`;
  } catch {
    return "just now";
  }
}

// --- UI Components ---
function TrackerCheckbox({
  checked,
  disabled,
}: {
  checked: boolean;
  disabled?: boolean;
}) {
  return (
    <span
      className={cx(
        "mt-1 inline-flex h-4 w-4 flex-none items-center justify-center rounded border transition-colors",
        checked
          ? "bg-[#7b2ee6]/90 border-[#7b2ee6] shadow-[0_0_12px_rgba(123,46,230,0.6)]"
          : "border-zinc-600",
        disabled ? "cursor-not-allowed opacity-70" : "group-hover:border-[#7b2ee6]/70",
      )}
      aria-checked={checked}
      role="checkbox"
    >
      {checked && <Check className="h-3 w-3 text-white" />}
    </span>
  );
}

function ProgressBar({ value, completedPercent = 0, ticks }: { value: number; completedPercent?: number; ticks: Record<number, number> }) {
  const clampedCompleted = Math.max(0, Math.min(completedPercent, value, 100));
  const tickKeys = Object.keys(ticks).map(Number);
  return (
    <div className="w-full">
      <div className="relative h-6 mb-1 select-none">
        {tickKeys.map((p) => (
          <div
            key={p}
            className="absolute -translate-x-1/2 text-[11px] text-zinc-300"
            style={{ left: `${ticks[p]}%` }}
          >
            {p}%
          </div>
        ))}
      </div>
      <div className="relative h-6 rounded-xl overflow-hidden bg-zinc-800/70 ring-1 ring-[#6c2ad0]/40">
        <div
          className="absolute inset-y-0 left-0"
          style={{
            width: `${value}%`,
            backgroundImage:
              "repeating-linear-gradient(135deg, rgba(123,46,230,0.5) 0, rgba(123,46,230,0.5) 8px, rgba(123,46,230,0.2) 8px, rgba(123,46,230,0.2) 16px)",
            backgroundColor: "rgba(73,17,126,0.55)",
            boxShadow:
              "0 0 24px rgba(123,46,230,0.55) inset, 0 0 12px rgba(123,46,230,0.4)",
          }}
        />
        {clampedCompleted > 0 && (
          <div
            className="absolute inset-y-0 left-0"
            style={{
              width: `${clampedCompleted}%`,
              backgroundImage:
                "repeating-linear-gradient(135deg, rgba(16,185,129,0.75) 0, rgba(16,185,129,0.75) 8px, rgba(6,95,70,0.8) 8px, rgba(6,95,70,0.8) 16px)",
              backgroundColor: "rgba(6,95,70,0.7)",
              boxShadow:
                "0 0 24px rgba(16,185,129,0.55) inset, 0 0 12px rgba(16,185,129,0.4)",
            }}
          />
        )}
        {tickKeys.map((p) => (
          <div
            key={`tick-${p}`}
            className="absolute top-0 bottom-0 w-px bg-zinc-300/60"
            style={{ left: `${ticks[p]}%` }}
          />
        ))}
      </div>
    </div>
  );
}

function MilestoneCard({
  data,
  onToggle,
  disabled = false,
  editMode = false,
  canEdit = false,
  onToggleEdit,
  onDeleteTask,
  onRenameTask,
  onAddTask,
  onRenameMilestone,
  onChangeMilestonePercent,
  onDeleteMilestone,
}: {
  data: MilestoneData;
  onToggle: (taskId: string) => void;
  disabled?: boolean;
  editMode?: boolean;
  canEdit?: boolean;
  onToggleEdit?: () => void;
  onDeleteTask?: (taskId: string) => void;
  onRenameTask?: (taskId: string, newText: string) => void;
  onAddTask?: () => void;
  onRenameMilestone?: (newTitle: string) => void;
  onChangeMilestonePercent?: (newPercent: number) => void;
  onDeleteMilestone?: () => void;
}) {
  const [open, setOpen] = useState(true);
  const [confirmDeleteMilestone, setConfirmDeleteMilestone] = useState(false);
  // Auto-expand when entering edit mode.
  const prevEditMode = useRef(editMode);
  useEffect(() => {
    if (editMode && !prevEditMode.current) setOpen(true);
    prevEditMode.current = editMode;
  }, [editMode]);
  // Reset delete confirmation when exiting edit mode.
  useEffect(() => {
    if (!editMode) setConfirmDeleteMilestone(false);
  }, [editMode]);
  const done = data.tasks.filter((t) => t.done).length;
  const total = data.tasks.length;
  const isComplete = total > 0 && done === total;
  return (
    <div
      className={cx(
        "rounded-2xl transition-all",
        isComplete
          ? "bg-emerald-500/15 ring-1 ring-emerald-500/60 shadow-[0_0_28px_rgba(16,185,129,0.35)]"
          : "bg-black/50 ring-1 ring-[#6c2ad0]/40",
        editMode
          ? "ring-[#7b2ee6]/70 shadow-[0_0_24px_rgba(123,46,230,0.25)]"
          : disabled ? "opacity-60" : "hover:ring-[#7b2ee6]/60 hover:shadow-[0_0_32px_rgba(123,46,230,0.2)]",
      )}
    >
      <div className="flex w-full items-center justify-between gap-3 p-4">
        <button
          onClick={() => !disabled && setOpen((v) => !v)}
          className="flex items-center gap-2 flex-1 min-w-0"
          disabled={disabled}
        >
          <ChevronDown
            className={cx(
              "h-4 w-4 text-zinc-300 transition-transform",
              open ? "rotate-180" : "rotate-0"
            )}
          />
          {editMode ? (
            <div className="flex items-center gap-2 flex-1 min-w-0" onClick={(e) => e.stopPropagation()}>
              <input
                type="text"
                value={data.title}
                onChange={(e) => onRenameMilestone?.(e.target.value)}
                placeholder="Milestone title…"
                className="flex-1 min-w-0 bg-transparent text-sm font-semibold text-zinc-100 tracking-wide border-b border-zinc-600 focus:border-[#7b2ee6] outline-none py-0.5"
                onClick={(e) => e.stopPropagation()}
              />
              <input
                type="number"
                value={data.percent}
                onChange={(e) => {
                  const val = Math.max(0, Math.min(100, Number(e.target.value) || 0));
                  onChangeMilestonePercent?.(val);
                }}
                min={0}
                max={100}
                className="w-16 bg-transparent text-sm text-zinc-200 border-b border-zinc-600 focus:border-[#7b2ee6] outline-none py-0.5 text-center"
                title="Milestone percent (0–100)"
                onClick={(e) => e.stopPropagation()}
              />
              <span className="text-xs text-zinc-500">%</span>
            </div>
          ) : (
            <h3 className="text-sm font-semibold text-zinc-100 tracking-wide">
              {data.title}
            </h3>
          )}
        </button>
        <div className="flex items-center gap-2">
          <div
            className={cx(
              "text-xs italic",
              isComplete ? "text-emerald-300" : "text-zinc-400",
            )}
          >
            {isComplete ? <>Complete ({done}/{total})</> : <>{done}/{total} tasks</>}
          </div>
          {canEdit && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onToggleEdit?.(); }}
              title={editMode ? "Exit edit mode" : "Edit tasks"}
              className={cx(
                "p-1 rounded-md transition-colors",
                editMode
                  ? "bg-[#7b2ee6]/20 hover:bg-[#7b2ee6]/30"
                  : "hover:bg-zinc-800",
              )}
            >
              <Pencil className={cx(
                "h-3.5 w-3.5 transition-colors",
                editMode ? "text-[#7b2ee6]" : "text-zinc-400 hover:text-[#7b2ee6]",
              )} />
            </button>
          )}
          {editMode && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setConfirmDeleteMilestone(true); }}
              title="Remove milestone"
              className="p-1 rounded-md transition-colors hover:bg-red-500/20"
            >
              <Trash2 className="h-3.5 w-3.5 text-zinc-500 hover:text-red-400" />
            </button>
          )}
        </div>
      </div>

      {/* Inline confirm for milestone deletion */}
      {confirmDeleteMilestone && (
        <div className="mx-4 mb-2 p-2 rounded-lg bg-red-500/10 ring-1 ring-red-500/40 flex items-center gap-3">
          <span className="text-xs text-red-200 flex-1">Delete this milestone and all its tasks?</span>
          <button
            type="button"
            onClick={() => { setConfirmDeleteMilestone(false); onDeleteMilestone?.(); }}
            className="text-xs font-medium text-red-300 hover:text-red-100 transition-colors"
          >
            Yes, delete
          </button>
          <button
            type="button"
            onClick={() => setConfirmDeleteMilestone(false)}
            className="text-xs text-zinc-400 hover:text-zinc-200 transition-colors"
          >
            Cancel
          </button>
        </div>
      )}

      {open ? <div className="h-px bg-zinc-800/70" /> : null}
      {open && (
        <ul className={cx("p-4 grid gap-2", editMode ? "grid-cols-1" : "sm:grid-cols-2")}>
          {data.tasks.map((t) => (
            <li
              key={t.id}
              onClick={() => {
                if (editMode || disabled) {
                  console.debug("[ProjectTracker] task click ignored", { taskId: t.id, editMode, disabled });
                  return;
                }
                console.debug("[ProjectTracker] task click", { taskId: t.id, milestone: data.title });
                onToggle(t.id);
              }}
              className={cx(
                "flex items-start gap-3",
                editMode ? "cursor-default" : disabled ? "cursor-not-allowed" : "cursor-pointer group",
              )}
            >
              <TrackerCheckbox checked={t.done} disabled={editMode || disabled} />
              {editMode ? (
                <>
                  <input
                    type="text"
                    value={t.text}
                    onChange={(e) => onRenameTask?.(t.id, e.target.value)}
                    placeholder="Task description…"
                    className="flex-1 min-w-0 bg-transparent text-sm text-zinc-200 border-b border-zinc-600 focus:border-[#7b2ee6] outline-none py-0.5"
                    autoFocus={!t.text}
                  />
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onDeleteTask?.(t.id); }}
                    className="flex-none p-0.5 rounded hover:bg-red-500/20 transition-colors"
                    title="Remove task"
                  >
                    <X className="h-3.5 w-3.5 text-zinc-500 hover:text-red-400" />
                  </button>
                </>
              ) : (
                <div
                  className={cx(
                    "text-sm leading-tight transition-colors",
                    t.done
                      ? "text-zinc-500 line-through"
                      : disabled
                        ? "text-zinc-400"
                        : "text-zinc-200 group-hover:text-white"
                  )}
                >
                  {t.text}
                </div>
              )}
            </li>
          ))}
          {editMode && (
            <li className="col-span-full">
              <button
                type="button"
                onClick={() => onAddTask?.()}
                className="flex items-center gap-1.5 text-xs text-zinc-400 hover:text-[#7b2ee6] transition-colors pt-1"
              >
                <Plus className="h-3.5 w-3.5" />
                Add task
              </button>
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

export default function ProjectTrackerPanel({
  tabId,
  projectId,
  projectName,
  clientName,
}: {
  tabId?: string;
  projectId?: string | null;
  projectName?: string | null;
  clientName?: string | null;
}) {
  const { setTabScope } = useWorkspace();
  const { user, isLoaded: userLoaded, isSignedIn } = useUser();

  const userHeaders = useMemo(() => {
    if (!userLoaded) return undefined;
    if (!isSignedIn) return undefined;
    const id = user?.id;
    if (!id) return undefined;
    const email = user?.primaryEmailAddress?.emailAddress || undefined;
    return { id, email };
  }, [userLoaded, isSignedIn, user?.id, user?.primaryEmailAddress?.emailAddress]);

  // Force periodic re-render so "Updated X min/hr ago" stays fresh even when data doesn't change.
  const [, forceUpdatedClockTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => {
      forceUpdatedClockTick((v) => (v + 1) % 1_000_000);
    }, 60_000);
    return () => window.clearInterval(id);
  }, []);

  const [trackerData, setTrackerData] = useState<DisciplineTracker[]>(cloneTrackerData(defaultData));
  const [active, setActive] = useState<string>("Mechanical");
  const [selectedClientKey, setSelectedClientKey] = useState<string>("");
  const [selectedLocationKey, setSelectedLocationKey] = useState<string>("");
  const [selectedProjectId, setSelectedProjectId] = useState<string>("");
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [projectPrimaries, setProjectPrimaries] = useState<ProjectPrimary[]>([]);
  const [loadingMembers, setLoadingMembers] = useState<boolean>(false);
  const [loadingTracker, setLoadingTracker] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [trackerUpdatedAt, setTrackerUpdatedAt] = useState<string | null>(null);
  const [trackerSaveBlocked, setTrackerSaveBlocked] = useState<boolean>(false);
  const [trackerProgressState, setTrackerProgressState] = useState<"read_only" | "active">("read_only");
  const trackerUpdatedAtRef = useRef<string | null>(null);
  const trackerProgressStateRef = useRef<"read_only" | "active">("read_only");
  const [editingMilestones, setEditingMilestones] = useState<number[]>([]);
  const [editBuffer, setEditBuffer] = useState<DisciplineTracker[] | null>(null);
  const editModeRef = useRef(false);
  const membersLoadSeqRef = useRef(0);

  // --- Template Library ---
  const [templates, setTemplates] = useState<{ id: string; template_name: string; discipline: string; updated_at: string }[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [showTemplateSaveModal, setShowTemplateSaveModal] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [applyingTemplate, setApplyingTemplate] = useState(false);
  const [canManageTemplate, setCanManageTemplate] = useState(false);
  const [canReadTemplate, setCanReadTemplate] = useState(false);
  const [companyName, setCompanyName] = useState<string>("");
  const [renamingTemplateId, setRenamingTemplateId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [templateDropdownOpen, setTemplateDropdownOpen] = useState(false);
  // Template edit modal state
  const [showTemplateEditModal, setShowTemplateEditModal] = useState(false);
  const [editTemplateId, setEditTemplateId] = useState<string | null>(null);
  const [editTemplateName, setEditTemplateName] = useState("");
  const [editTemplateDisciplines, setEditTemplateDisciplines] = useState<DisciplineTracker[]>([]);
  const [editTemplateDiscipline, setEditTemplateDiscipline] = useState("Mechanical");
  const [savingTemplateEdit, setSavingTemplateEdit] = useState(false);
  const templateDropdownRef = useRef<HTMLDivElement>(null);

  // Owner Requirements are now managed by OwnerRequirementsPanel (dedicated tool tab)

  // Close template dropdown on outside click
  useEffect(() => {
    if (!templateDropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (templateDropdownRef.current && !templateDropdownRef.current.contains(e.target as Node)) {
        setTemplateDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [templateDropdownOpen]);

  // Fetch RBAC permissions + company name for template capability
  useEffect(() => {
    if (!userHeaders?.id) {
      setCanManageTemplate(false);
      setCanReadTemplate(false);
      setCompanyName("");
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [rbacResp, companies] = await Promise.all([
          getMeRbac(userHeaders),
          listCompanies(userHeaders),
        ]);
        if (cancelled) return;
        const perms = Array.isArray((rbacResp as any)?.rbac?.permissions)
          ? ((rbacResp as any).rbac.permissions as string[])
          : [];
        setCanManageTemplate(perms.includes("company_tracker_templates:manage"));
        setCanReadTemplate(perms.includes("company_tracker_templates:read") || perms.includes("company_tracker_templates:manage"));
        const cName = Array.isArray(companies) && companies.length > 0 ? companies[0].name : "";
        setCompanyName(cName);
      } catch {
        if (!cancelled) {
          setCanManageTemplate(false);
          setCanReadTemplate(false);
          setCompanyName("");
        }
      }
    })();
    return () => { cancelled = true; };
  }, [userHeaders]);

  // fetchTemplates is defined after selectedProject useMemo (below)
  useEffect(() => {
    trackerUpdatedAtRef.current = trackerUpdatedAt;
  }, [trackerUpdatedAt]);

  useEffect(() => {
    trackerProgressStateRef.current = trackerProgressState;
  }, [trackerProgressState]);

  const refreshMembersForProject = useCallback(
    async (projectId: string) => {
      if (!userHeaders?.id || !projectId) {
        setProjectPrimaries([]);
        setLoadingMembers(false);
        return;
      }

      const seq = ++membersLoadSeqRef.current;
      setLoadingMembers(true);
      try {
        const primaries = await listProjectPrimaries(projectId, userHeaders);
        if (membersLoadSeqRef.current !== seq) return;
        setProjectPrimaries(Array.isArray(primaries) ? primaries : []);
      } catch (error) {
        console.warn("[ProjectTracker] failed to load project primaries", error);
        if (membersLoadSeqRef.current === seq) setProjectPrimaries([]);
      } finally {
        if (membersLoadSeqRef.current === seq) setLoadingMembers(false);
      }
    },
    [userHeaders],
  );

  const refreshTrackerFromServer = useCallback(
    async (projectId: string) => {
      if (!userHeaders?.id) return;
      if (!projectId) return;
      if (saving) return;
      if (loadingTracker) return;
      if (editModeRef.current) return;
      try {
        const state = await getProjectTracker(projectId, userHeaders);
        const nextProgressState = state?.progress_state === "active" ? "active" : "read_only";
        setTrackerProgressState(nextProgressState);
        trackerProgressStateRef.current = nextProgressState;
        const merged = mergeTrackerState(state);
        setTrackerData(merged);
        const nextUpdatedAt = state?.updated_at ?? null;
        trackerUpdatedAtRef.current = nextUpdatedAt;
        setTrackerUpdatedAt(nextUpdatedAt);
      } catch (error) {
        console.warn("[ProjectTracker] refresh poll failed", error);
      }
    },
    [loadingTracker, saving, userHeaders],
  );

  const normalizeClientKey = useCallback((proj: ProjectSummary): string => {
    const raw = typeof proj.client_name === "string" ? proj.client_name.trim() : "";
    return raw ? raw.toLowerCase() : "__unknown_client__";
  }, []);

  const normalizeLocationKey = useCallback((proj: ProjectSummary): string => {
    const cityRaw = typeof proj.city === "string" ? proj.city.trim() : "";
    const stateRaw = typeof proj.state === "string" ? proj.state.trim() : "";
    const city = cityRaw ? cityRaw.toUpperCase() : "";
    const state = stateRaw ? stateRaw.toUpperCase() : "";
    if (!city && !state) return "__unknown_location__";
    return `${city}||${state}`;
  }, []);

  const clientOptions = useMemo(() => {
    const map = new Map<string, { key: string; label: string }>();
    for (const p of projects) {
      const key = normalizeClientKey(p);
      const label =
        typeof p.client_name === "string" && p.client_name.trim() ? p.client_name.trim() : "Unknown client";
      if (!map.has(key)) map.set(key, { key, label });
    }
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [projects, normalizeClientKey]);

  const projectsForClient = useMemo(() => {
    if (!selectedClientKey) return [];
    return projects.filter((p) => normalizeClientKey(p) === selectedClientKey);
  }, [projects, normalizeClientKey, selectedClientKey]);

  const locationOptions = useMemo(() => {
    const map = new Map<string, { key: string; label: string }>();
    for (const p of projectsForClient) {
      const key = normalizeLocationKey(p);
      const city = typeof p.city === "string" && p.city.trim() ? p.city.trim().toUpperCase() : "";
      const state = typeof p.state === "string" && p.state.trim() ? p.state.trim().toUpperCase() : "";
      const label = city && state ? `${city}, ${state}` : city ? city : state ? state : "Unknown location";
      if (!map.has(key)) map.set(key, { key, label });
    }
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [projectsForClient, normalizeLocationKey]);

  const projectsForLocation = useMemo(() => {
    if (!selectedClientKey || !selectedLocationKey) return [];
    return projects.filter(
      (p) => normalizeClientKey(p) === selectedClientKey && normalizeLocationKey(p) === selectedLocationKey,
    );
  }, [projects, normalizeClientKey, normalizeLocationKey, selectedClientKey, selectedLocationKey]);

  const projectOptions = useMemo(() => {
    return projectsForLocation
      .slice()
      .sort((a, b) => (a.project_name || "").localeCompare(b.project_name || ""))
      .map((proj) => ({ value: proj.id, label: proj.project_name || "Untitled project" }));
  }, [projectsForLocation]);

  const selectedProject = useMemo(() => {
    if (!selectedProjectId) return null;
    return projects.find((proj) => proj.id === selectedProjectId) ?? null;
  }, [projects, selectedProjectId]);

  // Fetch template list when companyId + read permission available
  const fetchTemplates = useCallback(async () => {
    const companyId = selectedProject?.company_id;
    if (!companyId || !canReadTemplate || !userHeaders?.id) {
      setTemplates([]);
      return;
    }
    setLoadingTemplates(true);
    try {
      console.debug("[ProjectTracker:templates] fetching list", { companyId });
      const list = await listCompanyTrackerTemplates(companyId, userHeaders);
      setTemplates(list);
    } catch (e) {
      console.error("[ProjectTracker:templates] fetch list error", e);
      setTemplates([]);
    } finally {
      setLoadingTemplates(false);
    }
  }, [selectedProject?.company_id, canReadTemplate, userHeaders]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  useEffect(() => {
    if (!tabId) return;
    // If this tab was opened from a project shortcut, avoid clobbering the scope with null
    // before we initialize the dropdown selection.
    if (!selectedProjectId) {
      if (projectId) {
        setTabScope(tabId, {
          projectId,
          projectName: projectName ?? null,
          clientName: clientName ?? null,
          isMiscProject: false,
        });
        return;
      }
      setTabScope(tabId, { projectId: null, projectName: null, clientName: null, isMiscProject: false });
      return;
    }
    setTabScope(tabId, {
      projectId: selectedProjectId,
      projectName: selectedProject?.project_name ?? projectName ?? null,
      clientName: selectedProject?.client_name ?? clientName ?? null,
      isMiscProject: false,
    });
  }, [tabId, selectedProjectId, selectedProject, setTabScope, projectId, projectName, clientName]);

  useEffect(() => {
    refreshMembersForProject(selectedProjectId);
  }, [selectedProjectId, refreshMembersForProject]);

  useEffect(() => {
    const handler = (event: Event) => {
      const projectId = (event as CustomEvent<{ projectId?: string }>).detail?.projectId;
      if (!projectId) return;
      if (!selectedProjectId) return;
      if (projectId !== selectedProjectId) return;
      refreshMembersForProject(projectId);
    };
    window.addEventListener("workspace:project-members-updated", handler as EventListener);
    return () => window.removeEventListener("workspace:project-members-updated", handler as EventListener);
  }, [refreshMembersForProject, selectedProjectId]);

  const projectLocationLabel = useMemo(() => {
    if (!selectedProject) return "Unknown location";
    const city = typeof selectedProject.city === "string" ? selectedProject.city.trim().toUpperCase() : "";
    const state = typeof selectedProject.state === "string" ? selectedProject.state.trim().toUpperCase() : "";
    if (city && state) return `${city}, ${state}`;
    if (city) return city;
    if (state) return state;
    return "Unknown location";
  }, [selectedProject]);

  const primaryNames = useMemo(() => {
    const names = (projectPrimaries || [])
      .map((p) => {
        const profile = p?.profile ?? {};
        const name = typeof (profile as any)?.display_name === "string" ? String((profile as any).display_name).trim() : "";
        const email = typeof (profile as any)?.email === "string" ? String((profile as any).email).trim() : "";
        return name || email || p.user_id;
      })
      .filter(Boolean);
    // Preserve order but de-dupe.
    const seen = new Set<string>();
    const out: string[] = [];
    for (const n of names) {
      const key = n.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(n);
    }
    return out;
  }, [projectPrimaries]);

  useEffect(() => {
    if (!userHeaders) {
      setProjects([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const projectData = await listProjects({}, userHeaders);
        if (cancelled) return;
        const list = Array.isArray(projectData) ? projectData : [];
        setProjects(list);

        // If this tab was opened from a project shortcut, preselect that project once.
        if (projectId) {
          const exists = list.find((p) => p.id === projectId);
          if (exists) {
            const nextClient = normalizeClientKey(exists);
            const nextLocation = normalizeLocationKey(exists);
            setSelectedClientKey(nextClient);
            setSelectedLocationKey(nextLocation);
            setSelectedProjectId(exists.id);
          }
        }
      } catch (error) {
        console.error("[ProjectTracker] failed to load metadata", error);
        if (!cancelled) {
          setProjects([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userHeaders, projectId, normalizeClientKey, normalizeLocationKey]);

  useEffect(() => {
    const handler = (event: Event) => {
      const project = (event as CustomEvent<{ project?: ProjectSummary }>).detail?.project;
      if (!project?.id) return;
      setProjects((prev) => {
        const exists = prev.some((p) => p.id === project.id);
        if (exists) {
          return prev.map((p) => (p.id === project.id ? { ...p, ...project } : p));
        }
        return [...prev, project];
      });
      const nextClient = normalizeClientKey(project);
      const nextLocation = normalizeLocationKey(project);
      setSelectedClientKey(nextClient);
      setSelectedLocationKey(nextLocation);
      setSelectedProjectId(project.id);
      setTrackerData(cloneTrackerData(defaultData));
      setLoadError(null);
      setTrackerUpdatedAt(null);
    };
    window.addEventListener("workspace:project-created", handler as EventListener);
    return () => window.removeEventListener("workspace:project-created", handler as EventListener);
  }, [normalizeClientKey, normalizeLocationKey]);

  useEffect(() => {
    if (!userHeaders) {
      setSelectedClientKey("");
      setSelectedLocationKey("");
      setSelectedProjectId("");
    }
  }, [userHeaders]);

  useEffect(() => {
    // Normalize the 3-level selection any time project metadata or any selection changes.
    if (projects.length === 0) {
      if (selectedClientKey) setSelectedClientKey("");
      if (selectedLocationKey) setSelectedLocationKey("");
      if (selectedProjectId) setSelectedProjectId("");
      return;
    }

    // 1) Client
    const clientKeys = clientOptions.map((c) => c.key);
    const nextClient = selectedClientKey && clientKeys.includes(selectedClientKey) ? selectedClientKey : clientKeys[0] || "";
    if (nextClient !== selectedClientKey) {
      setSelectedClientKey(nextClient);
      return;
    }

    // 2) Location (depends on client)
    const locKeys = locationOptions.map((l) => l.key);
    const nextLoc =
      selectedLocationKey && locKeys.includes(selectedLocationKey) ? selectedLocationKey : locKeys[0] || "";
    if (nextLoc !== selectedLocationKey) {
      setSelectedLocationKey(nextLoc);
      return;
    }

    // 3) Project (depends on client+location)
    const projIds = projectOptions.map((p) => p.value);
    const nextProject =
      selectedProjectId && projIds.includes(selectedProjectId) ? selectedProjectId : projIds[0] || "";
    if (nextProject !== selectedProjectId) {
      setSelectedProjectId(nextProject);
    }
  }, [
    projects,
    clientOptions,
    locationOptions,
    projectOptions,
    selectedClientKey,
    selectedLocationKey,
    selectedProjectId,
  ]);

  useEffect(() => {
    if (!userHeaders?.id || !selectedProjectId) {
      setLoadingTracker(false);
      setLoadError(null);
      setTrackerUpdatedAt(null);
      setTrackerProgressState("read_only");
      setTrackerSaveBlocked(false);
      trackerProgressStateRef.current = "read_only";
      setTrackerData(cloneTrackerData(defaultData));
      return;
    }
    let cancelled = false;
    setLoadingTracker(true);
    setLoadError(null);
    setTrackerUpdatedAt(null);
    (async () => {
      try {
        const state = await getProjectTracker(selectedProjectId, userHeaders);
        if (cancelled) return;
        const nextProgressState = state?.progress_state === "active" ? "active" : "read_only";
        setTrackerProgressState(nextProgressState);
        setTrackerSaveBlocked(false);
        trackerProgressStateRef.current = nextProgressState;
        const merged = mergeTrackerState(state);
        setTrackerData(merged);
        const nextUpdatedAt = state?.updated_at ?? null;
        trackerUpdatedAtRef.current = nextUpdatedAt;
        setTrackerUpdatedAt(nextUpdatedAt);
        console.debug("[ProjectTracker] loaded tracker state", { projectId: selectedProjectId, state });
      } catch (error) {
        console.error("[ProjectTracker] failed to load tracker", error);
        if (!cancelled) {
          setLoadError("Failed to load project tracker");
          setTrackerProgressState("read_only");
          trackerProgressStateRef.current = "read_only";
          setTrackerData(cloneTrackerData(defaultData));
          setTrackerUpdatedAt(null);
        }
      } finally {
        if (!cancelled) setLoadingTracker(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedProjectId, userHeaders]);

  // Exit edit mode whenever the selected project changes.
  useEffect(() => {
    setEditingMilestones([]);
    setEditBuffer(null);
    editModeRef.current = false;
  }, [selectedProjectId]);

  // Poll the server so "Updated X min/hr ago" reflects changes from other users too.
  useEffect(() => {
    if (!userHeaders?.id || !selectedProjectId) return;
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      await refreshTrackerFromServer(selectedProjectId);
    };
    // Initial refresh after selecting project (covers cases where initial load used cached state)
    poll();
    const id = window.setInterval(poll, 60_000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [refreshTrackerFromServer, selectedProjectId, userHeaders?.id]);

  const persistTracker = useCallback(
    async (data: DisciplineTracker[], projectId: string): Promise<boolean> => {
      if (!userHeaders?.id || !projectId || trackerSaveBlocked) return false;
      try {
        setSaving(true);
        const payload = toProjectTrackerState(projectId, data, trackerUpdatedAtRef.current, "active");
        console.log("[ProjectTracker] saving payload", payload);
        const savedState = await saveProjectTracker(projectId, payload, userHeaders);
        console.log("[ProjectTracker] save response", savedState);
        const merged = mergeTrackerState(savedState);
        setTrackerData(merged);
        const newUpdatedAt = savedState?.updated_at ?? null;
        trackerUpdatedAtRef.current = newUpdatedAt;
        setTrackerUpdatedAt(newUpdatedAt);
        const newProgressState =
          savedState?.progress_state === "active" || savedState?.read_only === false ? "active" : "read_only";
        trackerProgressStateRef.current = newProgressState;
        setTrackerProgressState(newProgressState);
        return true;
      } catch (error: any) {
        console.error("[ProjectTracker] failed to save", error);
        const message = typeof error?.message === "string" ? error.message : "";
        const is405 = message.includes("405");
        const is403 = message.includes("403");
        const is401 = message.includes("401");
        const is409 = message.includes("409");

        // If the server refuses structural writes, block the edit UI to prevent repeated failures.
        if (is405 || is403) {
          setTrackerSaveBlocked(true);
          setTrackerProgressState("read_only");
          trackerProgressStateRef.current = "read_only";
        }

        // If we had an optimistic concurrency conflict, refresh latest server state.
        if (is409) {
          try {
            await refreshTrackerFromServer(projectId);
          } catch {
            // ignore
          }
        }
        toast({
          title: "Unable to save tracker",
          description:
            message && is405
              ? "Project tracker saving is not enabled for this environment yet."
              : message && is403
                ? "You don't have permission to make structural changes to this tracker. Tracker management access is required."
                : message && is401
                  ? "You're not authenticated. Please sign in again and retry."
                  : message || "Please try again.",
          variant: "destructive",
        });
        return false;
      } finally {
        setSaving(false);
      }
    },
    [refreshTrackerFromServer, trackerSaveBlocked, trackerUpdatedAtRef, userHeaders],
  );

  // --- Edit-mode capability gate (structural changes: manage permission only) ---
  const canEdit = Boolean(
    selectedProject?.capabilities?.project_trackers_manage &&
    trackerProgressState === "active" &&
    !loadingTracker &&
    !trackerSaveBlocked,
  );

  // --- Checkbox toggle capability gate (progress updates: update or manage permission) ---
  const canUpdateProgress = Boolean(
    (selectedProject?.capabilities?.project_trackers_update ||
      selectedProject?.capabilities?.project_trackers_manage) &&
    trackerProgressState === "active",
  );

  // --- Lock toggle capability gate (Project Manager or higher) ---
  const canToggleLock = Boolean(
    selectedProject?.capabilities?.project_trackers_lock &&
    selectedProjectId &&
    !loadingTracker,
  );

  const isTrackerLocked = trackerProgressState === "read_only";

  // --- Save as Template capability gate ---
  const canSaveTemplate = Boolean(
    canManageTemplate &&
    selectedProjectId &&
    trackerData.length > 0 &&
    !loadingTracker,
  );

  /** Build disciplines payload with done flags stripped */
  const buildTemplateMilestones = useCallback(() => {
    const disc = trackerData.find((d) => d.discipline === active);
    if (!disc) return [];
    return disc.milestones.map((m) => ({
      percent: m.percent,
      title: m.title,
      tasks: m.tasks.map((t) => ({
        id: t.id,
        text: t.text,
        done: false,
        hint: t.hint ?? null,
      })),
    }));
  }, [trackerData, active]);

  /** Save as new named template (saves the active discipline) */
  const handleSaveAsTemplate = useCallback(async () => {
    if (!selectedProject?.company_id || !userHeaders?.id || !templateName.trim() || !active) return;
    setShowTemplateSaveModal(false);
    setSavingTemplate(true);
    try {
      console.debug("[ProjectTracker:templates] creating", { name: templateName, discipline: active });
      const milestones = buildTemplateMilestones();
      await createCompanyTrackerTemplate(selectedProject.company_id, templateName.trim(), active, milestones, userHeaders);
      toast({
        title: "Template saved",
        description: `"${templateName.trim()}" (${active}) has been saved as a template.`,
      });
      setTemplateName("");
      fetchTemplates();
    } catch (error: any) {
      console.error("[ProjectTracker:templates] create error", error);
      toast({
        title: "Unable to save template",
        description: typeof error?.message === "string" ? error.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setSavingTemplate(false);
    }
  }, [companyName, selectedProject?.company_id, templateName, active, buildTemplateMilestones, userHeaders, fetchTemplates]);

  /** Apply a template to the current tracker (replaces the matching discipline's milestones) */
  const handleApplyTemplate = useCallback(async (templateId: string) => {
    if (!selectedProject?.company_id || !selectedProjectId || !userHeaders?.id) return;
    setApplyingTemplate(true);
    try {
      console.debug("[ProjectTracker:templates] applying", { templateId });
      const tpl = await getCompanyTrackerTemplateById(selectedProject.company_id, templateId, userHeaders);
      if (!tpl || !tpl.discipline) {
        toast({ title: "Template not found", variant: "destructive" });
        return;
      }
      const updated = trackerData.map((disc) => {
        if (disc.discipline !== tpl.discipline) return disc;
        return {
          ...disc,
          milestones: Array.isArray(tpl.milestones)
            ? tpl.milestones.map((m: any) => ({
                percent: m.percent,
                title: m.title || `${m.percent}% Progress`,
                tasks: Array.isArray(m.tasks) ? m.tasks.map((t: any) => ({ id: t.id, text: t.text, done: false, hint: t.hint ?? undefined })) : [],
              }))
            : disc.milestones,
        };
      });
      setTrackerData(updated);
      if (!updated.find((d) => d.discipline === active)) {
        setActive(DISCIPLINE_ORDER[0]);
      }
      await persistTracker(updated, selectedProjectId);
      toast({
        title: "Template applied",
        description: `"${tpl.template_name}" (${tpl.discipline}) has been applied.`,
      });
      setSelectedTemplateId(null);
    } catch (error: any) {
      console.error("[ProjectTracker:templates] apply error", error);
      toast({
        title: "Unable to apply template",
        description: typeof error?.message === "string" ? error.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setApplyingTemplate(false);
    }
  }, [selectedProject?.company_id, selectedProjectId, userHeaders, trackerData, active, persistTracker]);

  /** Update an existing template's milestones with current tracker state */
  const handleUpdateTemplate = useCallback(async (templateId: string) => {
    if (!selectedProject?.company_id || !userHeaders?.id) return;
    setSavingTemplate(true);
    try {
      console.debug("[ProjectTracker:templates] updating milestones", { templateId });
      const milestones = buildTemplateMilestones();
      await updateCompanyTrackerTemplate(selectedProject.company_id, templateId, { milestones }, userHeaders);
      toast({ title: "Template updated", description: "Template milestones have been overwritten with the current tracker state." });
      fetchTemplates();
    } catch (error: any) {
      console.error("[ProjectTracker:templates] update error", error);
      toast({ title: "Unable to update template", description: typeof error?.message === "string" ? error.message : "Please try again.", variant: "destructive" });
    } finally {
      setSavingTemplate(false);
    }
  }, [selectedProject?.company_id, buildTemplateMilestones, userHeaders, fetchTemplates]);

  /** Rename a template */
  const handleRenameTemplate = useCallback(async (templateId: string, newName: string) => {
    if (!selectedProject?.company_id || !userHeaders?.id || !newName.trim()) return;
    try {
      console.debug("[ProjectTracker:templates] renaming", { templateId, newName });
      await updateCompanyTrackerTemplate(selectedProject.company_id, templateId, { template_name: newName.trim() }, userHeaders);
      toast({ title: "Template renamed" });
      setRenamingTemplateId(null);
      setRenameValue("");
      fetchTemplates();
    } catch (error: any) {
      console.error("[ProjectTracker:templates] rename error", error);
      toast({ title: "Unable to rename template", description: typeof error?.message === "string" ? error.message : "Please try again.", variant: "destructive" });
    }
  }, [selectedProject?.company_id, userHeaders, fetchTemplates]);

  /** Delete a template */
  const handleDeleteTemplate = useCallback(async (templateId: string) => {
    if (!selectedProject?.company_id || !userHeaders?.id) return;
    try {
      console.debug("[ProjectTracker:templates] deleting", { templateId });
      await deleteCompanyTrackerTemplate(selectedProject.company_id, templateId, userHeaders);
      toast({ title: "Template deleted" });
      if (selectedTemplateId === templateId) setSelectedTemplateId(null);
      fetchTemplates();
    } catch (error: any) {
      console.error("[ProjectTracker:templates] delete error", error);
      toast({ title: "Unable to delete template", description: typeof error?.message === "string" ? error.message : "Please try again.", variant: "destructive" });
    } finally {
      setTemplateDropdownOpen(false);
    }
  }, [selectedProject?.company_id, userHeaders, selectedTemplateId, fetchTemplates]);

  /** Open the template edit modal by fetching the full template */
  const openTemplateEditModal = useCallback(async (templateId: string) => {
    if (!selectedProject?.company_id || !userHeaders?.id) return;
    setTemplateDropdownOpen(false);
    try {
      console.debug("[ProjectTracker:templates] opening edit modal", { templateId });
      const tpl = await getCompanyTrackerTemplateById(selectedProject.company_id, templateId, userHeaders);
      if (!tpl || !tpl.discipline) {
        toast({ title: "Template not found", variant: "destructive" });
        return;
      }
      const merged = mergeTrackerState({
        project_id: "template-edit",
        disciplines: [{ discipline: tpl.discipline, milestones: tpl.milestones }],
        updated_at: null,
        progress_state: "active",
      } as any);
      setEditTemplateId(tpl.id);
      setEditTemplateName(tpl.template_name);
      setEditTemplateDisciplines(merged);
      setEditTemplateDiscipline(tpl.discipline);
      setShowTemplateEditModal(true);
    } catch (error: any) {
      console.error("[ProjectTracker:templates] edit modal load error", error);
      toast({ title: "Unable to load template", description: error?.message || "Please try again.", variant: "destructive" });
    }
  }, [selectedProject?.company_id, userHeaders]);

  /** Save template edit modal changes */
  const handleSaveTemplateEdit = useCallback(async () => {
    if (!selectedProject?.company_id || !userHeaders?.id || !editTemplateId) return;
    setSavingTemplateEdit(true);
    try {
      console.debug("[ProjectTracker:templates] saving edit", { templateId: editTemplateId });
      const disc = editTemplateDisciplines.find((d) => d.discipline === editTemplateDiscipline);
      const milestones = disc
        ? disc.milestones.map((m) => ({
            percent: m.percent,
            title: m.title,
            tasks: m.tasks.map((t) => ({ id: t.id, text: t.text, done: false, hint: t.hint ?? null })),
          }))
        : [];
      await updateCompanyTrackerTemplate(selectedProject.company_id, editTemplateId, { milestones }, userHeaders);
      toast({ title: "Template updated", description: `"${editTemplateName}" has been saved.` });
      setShowTemplateEditModal(false);
      fetchTemplates();
    } catch (error: any) {
      console.error("[ProjectTracker:templates] save edit error", error);
      toast({ title: "Unable to save template", description: error?.message || "Please try again.", variant: "destructive" });
    } finally {
      setSavingTemplateEdit(false);
    }
  }, [selectedProject?.company_id, userHeaders, editTemplateId, editTemplateName, editTemplateDisciplines, editTemplateDiscipline, fetchTemplates]);

  // --- Template edit modal discipline-scoped callbacks ---
  const tplEditDeleteTask = useCallback((discipline: string, milestonePercent: number, taskId: string) => {
    setEditTemplateDisciplines((prev) =>
      prev.map((disc) => {
        if (disc.discipline !== discipline) return disc;
        return { ...disc, milestones: disc.milestones.map((m) => m.percent !== milestonePercent ? m : { ...m, tasks: m.tasks.filter((t) => t.id !== taskId) }) };
      }),
    );
  }, []);

  const tplEditRenameTask = useCallback((discipline: string, milestonePercent: number, taskId: string, newText: string) => {
    setEditTemplateDisciplines((prev) =>
      prev.map((disc) => {
        if (disc.discipline !== discipline) return disc;
        return { ...disc, milestones: disc.milestones.map((m) => m.percent !== milestonePercent ? m : { ...m, tasks: m.tasks.map((t) => t.id === taskId ? { ...t, text: newText } : t) }) };
      }),
    );
  }, []);

  const tplEditAddTask = useCallback((discipline: string, milestonePercent: number) => {
    setEditTemplateDisciplines((prev) =>
      prev.map((disc) => {
        if (disc.discipline !== discipline) return disc;
        const prefix = DISCIPLINE_PREFIX[disc.discipline as Discipline] || disc.discipline.toLowerCase().slice(0, 2);
        const newId = `${prefix}-${milestonePercent}-tpl-${Date.now()}`;
        return { ...disc, milestones: disc.milestones.map((m) => m.percent !== milestonePercent ? m : { ...m, tasks: [...m.tasks, { id: newId, text: "", done: false }] }) };
      }),
    );
  }, []);

  const tplEditRenameMilestone = useCallback((discipline: string, milestonePercent: number, newTitle: string) => {
    setEditTemplateDisciplines((prev) =>
      prev.map((disc) => {
        if (disc.discipline !== discipline) return disc;
        return { ...disc, milestones: disc.milestones.map((m) => m.percent === milestonePercent ? { ...m, title: newTitle } : m) };
      }),
    );
  }, []);

  const tplEditChangeMilestonePercent = useCallback((discipline: string, oldPercent: number, newPercent: number) => {
    setEditTemplateDisciplines((prev) =>
      prev.map((disc) => {
        if (disc.discipline !== discipline) return disc;
        return { ...disc, milestones: disc.milestones.map((m) => m.percent === oldPercent ? { ...m, percent: newPercent } : m) };
      }),
    );
  }, []);

  const tplEditDeleteMilestone = useCallback((discipline: string, milestonePercent: number) => {
    setEditTemplateDisciplines((prev) =>
      prev.map((disc) => {
        if (disc.discipline !== discipline) return disc;
        return { ...disc, milestones: disc.milestones.filter((m) => m.percent !== milestonePercent) };
      }),
    );
  }, []);

  const tplEditAddMilestone = useCallback((discipline: string) => {
    setEditTemplateDisciplines((prev) =>
      prev.map((disc) => {
        if (disc.discipline !== discipline) return disc;
        const existingPercents = disc.milestones.map((m) => m.percent);
        let newPercent = 50;
        for (const candidate of [25, 50, 75, 90, 100]) {
          if (!existingPercents.includes(candidate)) { newPercent = candidate; break; }
        }
        if (existingPercents.includes(newPercent)) {
          newPercent = Math.min(100, Math.max(...existingPercents, 0) + 10);
        }
        return { ...disc, milestones: [...disc.milestones, { percent: newPercent, title: `${newPercent}% Progress`, tasks: [] }] };
      }),
    );
  }, []);

  const editTemplateDisciplineData = useMemo(() => {
    return editTemplateDisciplines.find((d) => d.discipline === editTemplateDiscipline) ?? editTemplateDisciplines[0] ?? null;
  }, [editTemplateDisciplines, editTemplateDiscipline]);

  const toggleMilestoneEdit = useCallback((percent: number) => {
    setEditingMilestones((prev) => {
      const isAlreadyEditing = prev.includes(percent);
      if (isAlreadyEditing) {
        const next = prev.filter((p) => p !== percent);
        if (next.length === 0) editModeRef.current = false;
        return next;
      }
      // Snapshot tracker data when first milestone enters edit mode.
      if (prev.length === 0) {
        setEditBuffer(cloneTrackerData(trackerData));
        editModeRef.current = true;
      }
      return [...prev, percent];
    });
  }, [trackerData]);

  const cancelEdit = useCallback(() => {
    if (editBuffer) setTrackerData(editBuffer);
    setEditBuffer(null);
    setEditingMilestones([]);
    editModeRef.current = false;
  }, [editBuffer]);

  const saveEditChanges = useCallback(async () => {
    if (!selectedProjectId || !userHeaders?.id) return;
    const success = await persistTracker(trackerData, selectedProjectId);
    if (success) {
      setEditBuffer(null);
      setEditingMilestones([]);
      editModeRef.current = false;
    }
    // On failure, edit mode stays open — persistTracker already shows error toast
  }, [persistTracker, selectedProjectId, trackerData, userHeaders?.id]);

  const [togglingLock, setTogglingLock] = useState(false);

  const handleToggleLock = useCallback(async () => {
    if (!selectedProjectId || !userHeaders?.id) return;
    const nextLocked = trackerProgressState === "active";
    setTogglingLock(true);
    try {
      const savedState = await toggleTrackerLock(selectedProjectId, nextLocked, userHeaders);
      const nextProgressState =
        savedState?.progress_state === "active" || savedState?.read_only === false ? "active" : "read_only";
      setTrackerProgressState(nextProgressState);
      trackerProgressStateRef.current = nextProgressState;
      toast({
        title: nextLocked ? "Tracker locked" : "Tracker unlocked",
        description: nextLocked
          ? "This project tracker is now read-only."
          : "This project tracker is now editable.",
      });
    } catch (error: any) {
      console.error("[ProjectTracker] failed to toggle lock", error);
      toast({
        title: "Unable to change tracker lock",
        description: typeof error?.message === "string" ? error.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setTogglingLock(false);
    }
  }, [selectedProjectId, trackerProgressState, userHeaders]);

  const handleDeleteTask = useCallback(
    (discipline: string, milestonePercent: number, taskId: string) => {
      setTrackerData((prev) =>
        prev.map((disc) => {
          if (disc.discipline !== discipline) return disc;
          return {
            ...disc,
            milestones: disc.milestones.map((m) => {
              if (m.percent !== milestonePercent) return m;
              return { ...m, tasks: m.tasks.filter((t) => t.id !== taskId) };
            }),
          };
        }),
      );
    },
    [],
  );

  const handleRenameTask = useCallback(
    (discipline: string, milestonePercent: number, taskId: string, newText: string) => {
      setTrackerData((prev) =>
        prev.map((disc) => {
          if (disc.discipline !== discipline) return disc;
          return {
            ...disc,
            milestones: disc.milestones.map((m) => {
              if (m.percent !== milestonePercent) return m;
              return {
                ...m,
                tasks: m.tasks.map((t) => (t.id === taskId ? { ...t, text: newText } : t)),
              };
            }),
          };
        }),
      );
    },
    [],
  );

  const handleAddTask = useCallback(
    (discipline: string, milestonePercent: number) => {
      setTrackerData((prev) =>
        prev.map((disc) => {
          if (disc.discipline !== discipline) return disc;
          const prefix = DISCIPLINE_PREFIX[disc.discipline as Discipline] || disc.discipline.toLowerCase().slice(0, 2);
          return {
            ...disc,
            milestones: disc.milestones.map((m) => {
              if (m.percent !== milestonePercent) return m;
              const newId = `${prefix}-${milestonePercent}-custom-${Date.now()}`;
              return {
                ...m,
                tasks: [...m.tasks, { id: newId, text: "", done: false }],
              };
            }),
          };
        }),
      );
    },
    [],
  );

  const handleRenameMilestone = useCallback(
    (discipline: string, milestonePercent: number, newTitle: string) => {
      setTrackerData((prev) =>
        prev.map((disc) => {
          if (disc.discipline !== discipline) return disc;
          return {
            ...disc,
            milestones: disc.milestones.map((m) =>
              m.percent === milestonePercent ? { ...m, title: newTitle } : m,
            ),
          };
        }),
      );
    },
    [],
  );

  const handleChangeMilestonePercent = useCallback(
    (discipline: string, oldPercent: number, newPercent: number) => {
      setTrackerData((prev) =>
        prev.map((disc) => {
          if (disc.discipline !== discipline) return disc;
          return {
            ...disc,
            milestones: disc.milestones.map((m) =>
              m.percent === oldPercent ? { ...m, percent: newPercent } : m,
            ),
          };
        }),
      );
      // Update editingMilestones to track the new percent value.
      setEditingMilestones((prev) =>
        prev.map((p) => (p === oldPercent ? newPercent : p)),
      );
    },
    [],
  );

  const handleDeleteMilestone = useCallback(
    (discipline: string, milestonePercent: number) => {
      setTrackerData((prev) =>
        prev.map((disc) => {
          if (disc.discipline !== discipline) return disc;
          return {
            ...disc,
            milestones: disc.milestones.filter((m) => m.percent !== milestonePercent),
          };
        }),
      );
      setEditingMilestones((prev) => prev.filter((p) => p !== milestonePercent));
    },
    [],
  );

  const handleAddMilestone = useCallback(
    (discipline: string) => {
      setTrackerData((prev) =>
        prev.map((disc) => {
          if (disc.discipline !== discipline) return disc;
          // Find a reasonable default percent (next available 25-increment or max+10)
          const existingPercents = disc.milestones.map((m) => m.percent);
          let newPercent = 50;
          for (const candidate of [25, 50, 75, 90, 100]) {
            if (!existingPercents.includes(candidate)) {
              newPercent = candidate;
              break;
            }
          }
          if (existingPercents.includes(newPercent)) {
            const maxExisting = Math.max(...existingPercents, 0);
            newPercent = Math.min(100, maxExisting + 10);
          }
          const newMilestone: MilestoneData = {
            percent: newPercent,
            title: `${newPercent}% Progress`,
            tasks: [],
          };
          return {
            ...disc,
            milestones: [...disc.milestones, newMilestone],
          };
        }),
      );
      // Snapshot edit buffer if not already in edit mode.
      if (editingMilestones.length === 0) {
        setEditBuffer(cloneTrackerData(trackerData));
        editModeRef.current = true;
      }
    },
    [editingMilestones.length, trackerData],
  );

  const toggleTask = useCallback(
    (taskId: string) => {
      console.debug("[ProjectTracker] toggleTask invoked", {
        taskId,
        loadingTracker,
        saving,
        selectedProjectId,
        hasUser: Boolean(userHeaders?.id),
      });
      if (loadingTracker) return;
      if (saving) return;
      if (!selectedProjectId || !userHeaders?.id) return;

      let mutated = false;
      let progressContext: { discipline: string; milestonePercent: number; taskId: string; done: boolean } | null = null;
      const nowIso = new Date().toISOString();
      const nextState = trackerData.map((disc) => {
        let changed = false;
        const milestones = disc.milestones.map((milestone) => {
          let milestoneChanged = false;
          const tasks = milestone.tasks.map((task) => {
            if (task.id !== taskId) return task;
            milestoneChanged = true;
            changed = true;
            const nextDone = !task.done;
            progressContext = {
              discipline: disc.discipline,
              milestonePercent: milestone.percent ?? 0,
              taskId,
              done: nextDone,
            };
            return { ...task, done: nextDone };
          });
          if (!milestoneChanged) return milestone;
          return { ...milestone, tasks };
        });
        if (!changed) return disc;
        mutated = true;
        return { ...disc, milestones, updatedAt: nowIso };
      });

      if (!mutated || !progressContext) {
        console.debug("[ProjectTracker] toggleTask found no changes to persist", { taskId });
        return;
      }

      const snapshotState = trackerData;
      setTrackerData(nextState);

      const update = progressContext;
      const projectId = selectedProjectId;
      console.debug("[ProjectTracker] toggleTask persisting progress change", {
        taskId,
        projectId,
        ...update,
      });

      (async () => {
        try {
          const savedState = await updateTrackerProgress(
            projectId,
            { ...update, updated_at: trackerUpdatedAtRef.current },
            userHeaders,
          );
          const newUpdatedAt = savedState?.updated_at ?? null;
          trackerUpdatedAtRef.current = newUpdatedAt;
          setTrackerUpdatedAt(newUpdatedAt);
          const merged = mergeTrackerState(savedState);
          setTrackerData(merged);
        } catch (error: any) {
          console.error("[ProjectTracker] toggleTask failed to persist", error);
          setTrackerData(snapshotState);
          const message = typeof error?.message === "string" ? error.message : "";
          const is409 = message.includes("409") || message.includes("another session") || message.includes("locked");
          if (is409) {
            try {
              await refreshTrackerFromServer(projectId);
            } catch {}
          }
          toast({
            title: "Unable to update tracker progress",
            description:
              message.includes("403") || message.includes("Forbidden")
                ? "You don't have permission to update this tracker's progress."
                : message.includes("401")
                  ? "You're not authenticated. Please sign in again and retry."
                  : message.includes("locked") || message.includes("read_only")
                    ? "This tracker is locked and cannot be updated."
                    : message.includes("409") || message.includes("another session")
                      ? "Tracker was updated by another session. Refreshing..."
                      : "Please try again.",
            variant: "destructive",
          });
        }
      })();
    },
    [loadingTracker, refreshTrackerFromServer, saving, selectedProjectId, trackerData, trackerUpdatedAtRef, userHeaders],
  );

  const activeData = useMemo(() => {
    return trackerData.find((d) => d.discipline === active) ?? trackerData[0];
  }, [trackerData, active]);

  const { ticks: activeTicks, weights: activeWeights } = useMemo(() => {
    if (!activeData) return { ticks: {} as Record<number, number>, weights: {} as Record<number, number> };
    return deriveMilestoneMetrics(activeData.milestones);
  }, [activeData]);

  const overall = useMemo(() => {
    if (!activeData) return 0;
    const totalWeighted = activeData.milestones.reduce((acc, milestone) => {
      const weight = activeWeights[milestone.percent] ?? 0;
      const totalTasks = milestone.tasks.length;
      if (totalTasks === 0 || weight <= 0) return acc;
      const doneTasks = milestone.tasks.filter((t) => t.done).length;
      return acc + weight * (doneTasks / totalTasks);
    }, 0);
    return Math.round(totalWeighted * 100);
  }, [activeData, activeWeights]);

  const completedMilestonePercent = useMemo(() => {
    if (!activeData) return 0;
    const totalWeight = activeData.milestones.reduce((acc, m) => {
      const weight = activeWeights[m.percent] ?? 0;
      if (weight <= 0 || m.tasks.length === 0) return acc;
      const allDone = m.tasks.every((t) => t.done);
      return allDone ? acc + weight : acc;
    }, 0);
    if (totalWeight <= 0) return 0;
    return Math.min(100, Math.round(totalWeight * 100));
  }, [activeData, activeWeights]);

  const anyEditing = editingMilestones.length > 0;
  const togglesDisabled = loadingTracker || saving || !selectedProjectId || !userHeaders?.id || trackerSaveBlocked || anyEditing || isTrackerLocked || !canUpdateProgress;


  return (
    <div className="space-y-5 px-3 py-2">
      <div className="flex items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2 min-w-0">
          <span className="text-[11px] uppercase tracking-wide text-muted-foreground">Client</span>
          <select
            value={selectedClientKey}
            onChange={(e) => {
              const next = e.target.value;
              setSelectedClientKey(next);
              // Reset dependent dropdowns; normalization effect will pick defaults.
              setSelectedLocationKey("");
              setSelectedProjectId("");
            }}
            disabled={clientOptions.length === 0}
            className="text-sm border rounded px-2 py-1 text-white focus:outline-none focus:ring-1 focus:ring-[#6c2ad0]"
            style={{ backgroundColor: "#151515" }}
          >
            <option value="" disabled>
              {clientOptions.length === 0 ? "No clients available" : "Select a client"}
            </option>
            {clientOptions.map((opt) => (
              <option key={opt.key} value={opt.key}>
                {opt.label}
              </option>
            ))}
          </select>

          <span className="text-[11px] uppercase tracking-wide text-muted-foreground">Location</span>
          <select
            value={selectedLocationKey}
            onChange={(e) => {
              const next = e.target.value;
              setSelectedLocationKey(next);
              setSelectedProjectId("");
            }}
            disabled={!selectedClientKey || locationOptions.length === 0}
            className="text-sm border rounded px-2 py-1 text-white focus:outline-none focus:ring-1 focus:ring-[#6c2ad0]"
            style={{ backgroundColor: "#151515" }}
          >
            <option value="" disabled>
              {!selectedClientKey
                ? "Select a client first"
                : locationOptions.length === 0
                  ? "No locations available"
                  : "Select a location"}
            </option>
            {locationOptions.map((opt) => (
              <option key={opt.key} value={opt.key}>
                {opt.label}
              </option>
            ))}
          </select>

          <span className="text-[11px] uppercase tracking-wide text-muted-foreground">Project</span>
          <select
            value={selectedProjectId}
            onChange={(e) => {
              setSelectedProjectId(e.target.value);
            }}
            disabled={!selectedClientKey || !selectedLocationKey || projectOptions.length === 0}
            className="text-sm border rounded px-2 py-1 text-white focus:outline-none focus:ring-1 focus:ring-[#6c2ad0]"
            style={{ backgroundColor: "#151515" }}
          >
            <option value="" disabled>
              {!selectedClientKey
                ? "Select a client first"
                : !selectedLocationKey
                  ? "Select a location first"
                  : projectOptions.length === 0
                    ? "No projects available"
                    : "Select a project"}
            </option>
            {projectOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>

          <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
            <div>
              {saving ? "Saving…" : null}
              {loadError ? <span className="text-destructive">{loadError}</span> : null}
            </div>
          </div>
        </div>
        {canReadTemplate && (
          <div className="flex items-center gap-2 ml-auto flex-shrink-0">
            <span className="text-[11px] uppercase tracking-wide text-muted-foreground">Template</span>
            <div className="relative" ref={templateDropdownRef}>
              <button
                type="button"
                onClick={() => setTemplateDropdownOpen((v) => !v)}
                disabled={applyingTemplate || loadingTemplates || !selectedProjectId}
                className="text-sm border rounded px-2 py-1 text-white focus:outline-none focus:ring-1 focus:ring-[#6c2ad0] flex items-center gap-1.5 min-w-[140px] disabled:opacity-50"
                style={{ backgroundColor: "#151515" }}
              >
                <span className="flex-1 text-left truncate">
                  {loadingTemplates ? "Loading…" : applyingTemplate ? "Applying…" : selectedTemplateId ? (templates.find((t) => t.id === selectedTemplateId)?.template_name ?? "Select template") : templates.length === 0 ? "No templates" : "Select template"}
                </span>
                <ChevronDown className={cx("h-3 w-3 text-zinc-400 transition-transform flex-none", templateDropdownOpen && "rotate-180")} />
              </button>
                {templateDropdownOpen && (
                  <div className="absolute top-full right-0 mt-1 z-30 bg-zinc-900 rounded-lg ring-1 ring-zinc-700 shadow-xl py-1 min-w-[220px] max-h-[280px] overflow-y-auto">
                    {templates.length === 0 ? (
                      <div className="px-3 py-2 text-xs text-zinc-500 italic">No templates saved yet</div>
                    ) : (
                      templates.map((t) => (
                        <div
                          key={t.id}
                          className="group flex items-center gap-1 px-2 py-1.5 hover:bg-zinc-800 transition-colors"
                        >
                          {renamingTemplateId === t.id ? (
                            <form
                              onSubmit={(e) => { e.preventDefault(); handleRenameTemplate(t.id, renameValue); }}
                              className="flex items-center gap-1 flex-1"
                            >
                              <input
                                type="text"
                                value={renameValue}
                                onChange={(e) => setRenameValue(e.target.value)}
                                className="flex-1 bg-zinc-800 text-xs text-zinc-100 rounded px-1.5 py-0.5 ring-1 ring-zinc-600 focus:ring-[#7b2ee6] focus:outline-none"
                                autoFocus
                                onKeyDown={(e) => { if (e.key === "Escape") { setRenamingTemplateId(null); setRenameValue(""); } }}
                              />
                              <button type="submit" className="p-0.5 text-[#7b2ee6] hover:text-[#9b5af5]" title="Confirm"><Check className="h-3 w-3" /></button>
                              <button type="button" onClick={() => { setRenamingTemplateId(null); setRenameValue(""); }} className="p-0.5 text-zinc-500 hover:text-zinc-300" title="Cancel"><X className="h-3 w-3" /></button>
                            </form>
                          ) : (
                            <>
                              <button
                                type="button"
                                onClick={() => {
                                  setTemplateDropdownOpen(false);
                                  setSelectedTemplateId(t.id);
                                  if (window.confirm(`Apply "${t.template_name}" (${t.discipline})? This will replace the ${t.discipline} milestones and tasks.`)) {
                                    handleApplyTemplate(t.id);
                                  } else {
                                    setSelectedTemplateId(null);
                                  }
                                }}
                                className="flex-1 text-left text-xs text-zinc-200 truncate"
                                title={`Apply "${t.template_name}" (${t.discipline})`}
                              >
                                {t.template_name}
                              </button>
                              {canManageTemplate && (
                                <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); openTemplateEditModal(t.id); }}
                                    className="p-1 rounded hover:bg-zinc-700 transition-colors"
                                    title="Edit template"
                                  >
                                    <Pencil className="h-3 w-3 text-zinc-400 hover:text-[#7b2ee6]" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); setRenamingTemplateId(t.id); setRenameValue(t.template_name); }}
                                    className="p-1 rounded hover:bg-zinc-700 transition-colors"
                                    title="Rename template"
                                  >
                                    <span className="text-[10px] text-zinc-400 hover:text-[#7b2ee6] font-medium">Aa</span>
                                  </button>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (window.confirm(`Delete "${t.template_name}" (${t.discipline})? This cannot be undone.`)) {
                                        handleDeleteTemplate(t.id);
                                        setTemplateDropdownOpen(false);
                                      }
                                    }}
                                    className="p-1 rounded hover:bg-red-500/20 transition-colors"
                                    title="Delete template"
                                  >
                                    <Trash2 className="h-3 w-3 text-zinc-400 hover:text-red-400" />
                                  </button>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap items-center gap-2">
        {DISCIPLINE_ORDER.map((name) => {
          const meta = DISCIPLINE_META[name] ?? { icon: <BarChart3 className="h-4 w-4" />, accent: "from-zinc-500/30", tabTint: "text-zinc-300" };
          const activeTab = name === active;
          return (
            <button
              key={name}
              onClick={() => setActive(name)}
              className={cx(
                "flex items-center gap-2 rounded-full px-4 py-2 text-sm ring-1 ring-[#6c2ad0]/40 bg-black/40 hover:ring-[#7b2ee6]/60",
                activeTab ? "shadow-[0_0_24px_rgba(123,46,230,0.25)]" : "opacity-85",
              )}
              style={{
                backgroundImage: activeTab ? `linear-gradient(90deg, ${meta.accent}, transparent)` : undefined,
              }}
            >
              <span className={cx("text-zinc-200", activeTab && meta.tabTint)}>{meta.icon}</span>
              <span className="tracking-wide text-zinc-100">{name}</span>
            </button>
          );
        })}
      </div>

      <header className="rounded-3xl bg-black/40 ring-1 ring-[#6c2ad0]/40 p-5 shadow-[0_0_40px_rgba(123,46,230,0.15)]">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold tracking-wide flex items-center gap-2">
            <BarChart3 className="h-5 w-5" /> {activeData?.discipline ?? "Discipline"}
          </h2>
          <div className="flex items-center gap-3">
            <div
              className="text-xs text-zinc-400 italic opacity-80"
              title={activeData?.updatedAt ? new Date(activeData.updatedAt).toLocaleString() : undefined}
            >
              {loadingTracker ? "Loading…" : <>Updated {formatUpdated(activeData?.updatedAt)}</>}
              {saving ? " • Saving…" : null}
            </div>
            {canToggleLock && (
              <button
                type="button"
                onClick={handleToggleLock}
                disabled={togglingLock}
                title={isTrackerLocked ? "Unlock tracker — allow edits" : "Lock tracker — prevent edits"}
                className={cx(
                  "p-1.5 rounded-md transition-colors disabled:opacity-50",
                  isTrackerLocked
                    ? "bg-red-500/15 hover:bg-red-500/25 ring-1 ring-red-500/30"
                    : "hover:bg-zinc-800 ring-1 ring-zinc-700/50",
                )}
              >
                {isTrackerLocked ? (
                  <Lock className="h-3.5 w-3.5 text-red-400" />
                ) : (
                  <Unlock className="h-3.5 w-3.5 text-zinc-400 hover:text-zinc-200" />
                )}
              </button>
            )}
          </div>
        </div>
        <div className="mt-1 text-xs text-zinc-400 space-y-0.5">
          {selectedProject ? (
            <>
              <div>
                {projectLocationLabel} - {selectedProject.project_name ?? "Project"}
              </div>
              <div>{(selectedProject.client_name || "").trim() || "Unknown client"}</div>
              <div>
                {(primaryNames.length === 1 ? "Primary" : "Primaries")}:{" "}
                {loadingMembers ? "Loading…" : primaryNames.length > 0 ? primaryNames.join(", ") : "—"}
              </div>
            </>
          ) : (
            <div>{projectOptions.length === 0 ? "No projects available" : "Select a project to view tracker"}</div>
          )}
        </div>
        <div className="mt-4">
          <ProgressBar value={overall} completedPercent={completedMilestonePercent} ticks={activeTicks} />
          {selectedProjectId && (
            <button
              type="button"
              onClick={() => {
                window.dispatchEvent(
                  new CustomEvent("workspace:open-tab", {
                    detail: {
                      type: "owner-requirements",
                      projectId: selectedProjectId,
                      projectName: selectedProject?.project_name ?? null,
                      clientName: selectedProject?.client_name ?? null,
                    },
                  }),
                );
              }}
              className="mt-2 inline-flex items-center gap-1 rounded-full border border-primary/45 bg-primary/12 px-2 py-0.5 text-[11px] text-primary shadow-[0_0_0_1px_hsl(var(--primary)/0.15),0_0_0px_hsl(var(--primary)/0)] transition-[background-color,border-color,box-shadow,color] duration-200 ease-out hover:border-primary/65 hover:bg-primary/22 hover:shadow-[0_0_0_1px_hsl(var(--primary)/0.22),0_0_6px_hsl(var(--primary)/0.42)]"
            >
              owner requirements
            </button>
          )}
        </div>
      </header>

      {anyEditing && (
        <div className="flex items-center gap-3 px-1">
          <button
            onClick={saveEditChanges}
            disabled={saving}
            className="rounded-lg bg-[#7b2ee6] px-4 py-1.5 text-sm font-medium text-white hover:bg-[#6c2ad0] disabled:opacity-50 transition-colors"
          >
            {saving ? "Saving…" : "Save Changes"}
          </button>
          <button
            onClick={cancelEdit}
            disabled={saving}
            className="rounded-lg bg-zinc-800 px-4 py-1.5 text-sm font-medium text-zinc-300 hover:bg-zinc-700 disabled:opacity-50 transition-colors ring-1 ring-zinc-700"
          >
            Cancel
          </button>
          <span className="text-xs text-zinc-500 italic">Editing task list — changes are local until saved</span>
        </div>
      )}

      {trackerSaveBlocked ? (
        <div className="rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-[11px] text-red-200">
          Project tracker saving is not enabled for this environment yet; checklist changes will not be persisted.
        </div>
      ) : null}

      {isTrackerLocked && !trackerSaveBlocked && selectedProjectId ? (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-[11px] text-amber-200 flex items-center gap-2">
          <Lock className="h-3.5 w-3.5 flex-shrink-0" />
          This project tracker is locked. {canToggleLock ? "Use the lock icon above to unlock." : "A project manager can unlock it."}
        </div>
      ) : null}

      <section className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
        {activeData?.milestones.map((m) => (
          <MilestoneCard
            key={`${m.percent}-${m.title}`}
            data={m}
            onToggle={toggleTask}
            disabled={togglesDisabled}
            editMode={editingMilestones.includes(m.percent)}
            canEdit={canEdit}
            onToggleEdit={() => toggleMilestoneEdit(m.percent)}
            onDeleteTask={(taskId) => handleDeleteTask(active, m.percent, taskId)}
            onRenameTask={(taskId, text) => handleRenameTask(active, m.percent, taskId, text)}
            onAddTask={() => handleAddTask(active, m.percent)}
            onRenameMilestone={(title) => handleRenameMilestone(active, m.percent, title)}
            onChangeMilestonePercent={(pct) => handleChangeMilestonePercent(active, m.percent, pct)}
            onDeleteMilestone={() => handleDeleteMilestone(active, m.percent)}
          />
        ))}
      </section>

      {/* Add milestone button (visible in edit mode or when canEdit) */}
      {canEdit && (
        <div className="px-1">
          <button
            type="button"
            onClick={() => handleAddMilestone(active)}
            className="flex items-center gap-1.5 text-xs text-zinc-400 hover:text-[#7b2ee6] transition-colors"
          >
            <Plus className="h-3.5 w-3.5" />
            Add milestone
          </button>
        </div>
      )}

      {/* Save as Template button */}
      {canSaveTemplate && (
        <div className="px-1">
          <button
            type="button"
            onClick={() => { setTemplateName(""); setShowTemplateSaveModal(true); }}
            disabled={savingTemplate}
            className="flex items-center gap-2 rounded-lg bg-zinc-800 px-4 py-1.5 text-sm font-medium text-zinc-200 hover:bg-zinc-700 disabled:opacity-50 transition-colors ring-1 ring-zinc-700"
          >
            <Save className="h-3.5 w-3.5" />
            {savingTemplate ? "Saving…" : "Save as Template"}
          </button>
        </div>
      )}

      {/* Template edit modal */}
      {showTemplateEditModal && editTemplateDisciplines.length > 0 && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ backgroundColor: "rgba(0, 0, 0, 0.7)" }}
          onClick={() => setShowTemplateEditModal(false)}
        >
          <div
            className="bg-zinc-900 rounded-2xl ring-1 ring-[#6c2ad0]/50 shadow-[0_0_60px_rgba(123,46,230,0.25)] p-6 w-full max-w-6xl mx-4 max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal header */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-zinc-100 flex items-center gap-2">
                <Pencil className="h-4 w-4 text-[#7b2ee6]" />
                Edit Template: <span className="text-[#b396ff]">{editTemplateName}</span>
              </h3>
              <div className="flex items-center gap-3">
                <span className="text-[11px] uppercase tracking-wide text-muted-foreground">Discipline</span>
                <select
                  value={editTemplateDiscipline}
                  onChange={(e) => setEditTemplateDiscipline(e.target.value)}
                  className="text-sm border rounded px-2 py-1 text-white focus:outline-none focus:ring-1 focus:ring-[#6c2ad0]"
                  style={{ backgroundColor: "#151515" }}
                >
                  {editTemplateDisciplines.map((d) => (
                    <option key={d.discipline} value={d.discipline}>{d.discipline}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setShowTemplateEditModal(false)}
                  className="p-1.5 rounded-md hover:bg-zinc-800 transition-colors"
                  title="Close"
                >
                  <X className="h-4 w-4 text-zinc-400" />
                </button>
              </div>
            </div>

            {/* Scrollable milestone cards */}
            <div className="flex-1 overflow-y-auto space-y-4 px-2 py-1">
              {editTemplateDisciplineData?.milestones.map((m) => (
                <MilestoneCard
                  key={`tpl-${editTemplateDiscipline}-${m.percent}-${m.title}`}
                  data={m}
                  onToggle={() => {}}
                  disabled={false}
                  editMode={true}
                  canEdit={true}
                  onToggleEdit={() => {}}
                  onDeleteTask={(taskId) => tplEditDeleteTask(editTemplateDiscipline, m.percent, taskId)}
                  onRenameTask={(taskId, text) => tplEditRenameTask(editTemplateDiscipline, m.percent, taskId, text)}
                  onAddTask={() => tplEditAddTask(editTemplateDiscipline, m.percent)}
                  onRenameMilestone={(title) => tplEditRenameMilestone(editTemplateDiscipline, m.percent, title)}
                  onChangeMilestonePercent={(pct) => tplEditChangeMilestonePercent(editTemplateDiscipline, m.percent, pct)}
                  onDeleteMilestone={() => tplEditDeleteMilestone(editTemplateDiscipline, m.percent)}
                />
              ))}
              <button
                type="button"
                onClick={() => tplEditAddMilestone(editTemplateDiscipline)}
                className="flex items-center gap-1.5 text-xs text-zinc-400 hover:text-[#7b2ee6] transition-colors"
              >
                <Plus className="h-3.5 w-3.5" />
                Add milestone
              </button>
            </div>

            {/* Modal footer */}
            <div className="flex items-center gap-3 justify-end mt-4 pt-4 border-t border-zinc-800">
              <button
                type="button"
                onClick={() => setShowTemplateEditModal(false)}
                className="rounded-lg bg-zinc-800 px-4 py-1.5 text-sm font-medium text-zinc-300 hover:bg-zinc-700 transition-colors ring-1 ring-zinc-700"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveTemplateEdit}
                disabled={savingTemplateEdit}
                className="rounded-lg bg-[#7b2ee6] px-4 py-1.5 text-sm font-medium text-white hover:bg-[#6c2ad0] disabled:opacity-50 transition-colors"
              >
                {savingTemplateEdit ? "Saving…" : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Save as Template modal (name prompt) */}
      {showTemplateSaveModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ backgroundColor: "rgba(0, 0, 0, 0.6)" }}
          onClick={() => setShowTemplateSaveModal(false)}
        >
          <div
            className="bg-zinc-900 rounded-2xl ring-1 ring-[#6c2ad0]/50 shadow-[0_0_60px_rgba(123,46,230,0.25)] p-6 max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-base font-semibold text-zinc-100 mb-3">
              Save as Template
            </h3>
            <p className="text-sm text-zinc-300 mb-4">
              Save the current <span className="font-semibold text-zinc-100">{active}</span> milestones and tasks as a named template for{" "}
              <span className="font-semibold text-zinc-100">{companyName || "your company"}</span>.
            </p>
            <input
              type="text"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              placeholder="Template name"
              maxLength={100}
              className="w-full bg-zinc-800 text-sm text-zinc-100 rounded-lg px-3 py-2 ring-1 ring-zinc-700 focus:ring-[#7b2ee6] focus:outline-none mb-5"
              autoFocus
              onKeyDown={(e) => { if (e.key === "Enter" && templateName.trim()) handleSaveAsTemplate(); }}
            />
            <div className="flex items-center gap-3 justify-end">
              <button
                type="button"
                onClick={() => setShowTemplateSaveModal(false)}
                className="rounded-lg bg-zinc-800 px-4 py-1.5 text-sm font-medium text-zinc-300 hover:bg-zinc-700 transition-colors ring-1 ring-zinc-700"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveAsTemplate}
                disabled={savingTemplate || !templateName.trim()}
                className="rounded-lg bg-[#7b2ee6] px-4 py-1.5 text-sm font-medium text-white hover:bg-[#6c2ad0] disabled:opacity-50 transition-colors"
              >
                {savingTemplate ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      <p className="text-xs text-zinc-400 italic">
        Addendum items tracked separately – not counted as checklist tasks.
      </p>

    </div>
  );
}
